/* eslint-disable no-unused-vars */
import React, { useDeferredValue, useEffect, useMemo, useState } from "react";
import "./tagihanTable.scss";
import Swal from "sweetalert2";
import { getUsersWithTagihan, updateTagihan, updateTagihanFoto } from "../api";
import Loader from "./loader.js";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Edit2, X, Save } from "lucide-react";

const TagihanTable = () => {
  const [search, setSearch] = useState("");
  const [tagihanData, setTagihanData] = useState([]);
  const [editingTagihan, setEditingTagihan] = useState(null);
  const [editedValues, setEditedValues] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [fotoKtp, setFotoKtp] = useState(null);
  const [previewKtp, setPreviewKtp] = useState(null);

  const fetchTagihan = async () => {
    setIsLoading(true);
    try {
      const token =
        localStorage.getItem("adminToken") ||
        sessionStorage.getItem("adminToken");
      const users = await getUsersWithTagihan(token);

      const allTagihan = [];
      users.forEach((user) => {
        if (Array.isArray(user.info_pinjam)) {
          user.info_pinjam.forEach((pinjam) => {
            if (pinjam.total_tagihan && pinjam.total_tagihan > 0) {
              allTagihan.push({
                id: pinjam._id,
                nama: pinjam.nama_nasabah || user.name || "-",
                tanggalJatuhTempo: pinjam.tanggal_jatuh_tempo || "",
                noTelepon: user.phone || "-",
                nik: pinjam.no_ktp || "-",
                totalTagihanRaw: pinjam.total_tagihan,
                totalTagihan: `Rp ${Number(pinjam.total_tagihan).toLocaleString(
                  "id-ID"
                )},00`,
                produk_pinjaman: pinjam.produk_pinjaman || "",
                bank_virtual_account1: pinjam.bank_virtual_account1 || "",
                bank_virtual_account2: pinjam.bank_virtual_account2 || "",
                nomor_virtual_account1: pinjam.nomor_virtual_account1 || "",
                nomor_virtual_account2: pinjam.nomor_virtual_account2 || "",
              });
            }
          });
        }
      });

      setTagihanData(allTagihan);
    } catch (err) {
      setTagihanData([]);
      console.error("Gagal memuat data tagihan", err);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchTagihan();
  }, []);

  const deferredSearch = useDeferredValue(search);
  const filteredData = useMemo(() => {
    const keyword = deferredSearch.trim().toLowerCase();
    if (!keyword) return tagihanData;

    return tagihanData.filter((tagihan) =>
      [tagihan.nama, tagihan.noTelepon, tagihan.nik, tagihan.totalTagihan]
        .join(" ")
        .toLowerCase()
        .includes(keyword)
    );
  }, [tagihanData, deferredSearch]);

  const handleEditClick = (tagihan) => {
    setEditingTagihan(tagihan);
    setEditedValues({
      totalTagihanRaw: tagihan.totalTagihanRaw,
      tanggalJatuhTempo: tagihan.tanggalJatuhTempo.split("T")[0],
      produkPinjaman: tagihan.produk_pinjaman || "",
      bank_virtual_account1: tagihan.bank_virtual_account1 || "",
      bank_virtual_account2: tagihan.bank_virtual_account2 || "",
      nomor_virtual_account1: tagihan.nomor_virtual_account1 || "",
      nomor_virtual_account2: tagihan.nomor_virtual_account2 || "",
    });
    setFotoKtp(null);
    setPreviewKtp(tagihan.foto_ktp_url || null);
  };

  const handleFotoKtpChange = (e) => {
    const file = e.target.files[0];
    setFotoKtp(file);
    setPreviewKtp(file ? URL.createObjectURL(file) : null);
  };

  const handleClose = () => {
    setEditingTagihan(null);
    setEditedValues({});
    setFotoKtp(null);
    setPreviewKtp(null);
  };

  const handleSave = async () => {
    if (
      !editedValues.totalTagihanRaw ||
      !editedValues.tanggalJatuhTempo ||
      !editedValues.produkPinjaman
    ) {
      Swal.fire("Peringatan", "Semua field wajib diisi!", "warning");
      return;
    }

    const token =
      localStorage.getItem("adminToken") ||
      sessionStorage.getItem("adminToken");

    try {
      await updateTagihan(
        editingTagihan.id,
        {
          total_tagihan: editedValues.totalTagihanRaw,
          tanggal_jatuh_tempo: editedValues.tanggalJatuhTempo,
          produk_pinjaman: editedValues.produkPinjaman,
          bank_virtual_account1: editedValues.bank_virtual_account1,
          bank_virtual_account2: editedValues.bank_virtual_account2,
          nomor_virtual_account1: editedValues.nomor_virtual_account1,
          nomor_virtual_account2: editedValues.nomor_virtual_account2,
        },
        token
      );

      if (fotoKtp) {
        const formData = new FormData();
        formData.append("fotoKtp", fotoKtp);
        await updateTagihanFoto(editingTagihan.id, formData, token);
      }

      handleClose();
      await Swal.fire("Berhasil", "Tagihan berhasil diupdate", "success");
      fetchTagihan();
    } catch (err) {
      console.error(err);
      handleClose();
      Swal.fire("Error", "Terjadi kesalahan saat menyimpan tagihan", "error");
    }
  };

  return (
    <div className="tagihan-container">
      <div className="table-header">
        <h3>Tagihan Belum Lunas</h3>
        <div style={{ position: 'relative' }}>
          <Search size={18} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
          <input
            type="text"
            className="search-field"
            placeholder="Cari data..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ paddingLeft: 40 }}
          />
        </div>
      </div>

      {isLoading ? (
        <Loader />
      ) : (
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Nama</th>
                <th>Jatuh Tempo</th>
                <th>No Telepon</th>
                <th>NIK</th>
                <th>Total Tagihan</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: "center", padding: "30px", color: "var(--text-secondary)" }}>
                    Tidak ada data tagihan ditemukan.
                  </td>
                </tr>
              ) : (
                filteredData.map((tagihan) => (
                  <motion.tr
                    key={tagihan.id}
                    initial={false}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.15 }}
                  >
                    <td>{tagihan.nama}</td>
                    <td>{new Date(tagihan.tanggalJatuhTempo).toLocaleDateString("id-ID")}</td>
                    <td>{tagihan.noTelepon}</td>
                    <td>{tagihan.nik}</td>
                    <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{tagihan.totalTagihan}</td>
                    <td>
                      <button
                        className="edit-btn"
                        title="Edit Tagihan"
                        onClick={() => handleEditClick(tagihan)}
                      >
                        <Edit2 size={18} />
                      </button>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <AnimatePresence>
        {editingTagihan && (
          <div className="modal-overlay">
            <motion.div
              className="modal-content"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h4>Edit Tagihan</h4>
                <button onClick={handleClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                  <X size={24} color="var(--text-secondary)" />
                </button>
              </div>

              <div className="form-group">
                <label>Produk Pinjaman</label>
                <input
                  type="text"
                  value={editedValues.produkPinjaman}
                  onChange={(e) => setEditedValues({ ...editedValues, produkPinjaman: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Tanggal Jatuh Tempo</label>
                <input
                  type="date"
                  value={editedValues.tanggalJatuhTempo}
                  onChange={(e) => setEditedValues({ ...editedValues, tanggalJatuhTempo: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Total Tagihan (Rp)</label>
                <input
                  type="number"
                  value={editedValues.totalTagihanRaw}
                  onChange={(e) => setEditedValues({ ...editedValues, totalTagihanRaw: e.target.value })}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div className="form-group">
                  <label>Bank VA 1</label>
                  <input
                    type="text"
                    value={editedValues.bank_virtual_account1}
                    onChange={(e) => setEditedValues({ ...editedValues, bank_virtual_account1: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>No VA 1</label>
                  <input
                    type="text"
                    value={editedValues.nomor_virtual_account1}
                    onChange={(e) => setEditedValues({ ...editedValues, nomor_virtual_account1: e.target.value })}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div className="form-group">
                  <label>Bank VA 2</label>
                  <input
                    type="text"
                    value={editedValues.bank_virtual_account2}
                    onChange={(e) => setEditedValues({ ...editedValues, bank_virtual_account2: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>No VA 2</label>
                  <input
                    type="text"
                    value={editedValues.nomor_virtual_account2}
                    onChange={(e) => setEditedValues({ ...editedValues, nomor_virtual_account2: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Foto KTP</label>
                <input type="file" accept="image/*" onChange={handleFotoKtpChange} />
                {previewKtp && <img src={previewKtp} alt="Preview KTP" className="preview-image" />}
              </div>

              <div className="modal-actions">
                <button className="cancel-btn" onClick={handleClose}>Batal</button>
                <button className="save-btn" onClick={handleSave}>Simpan</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TagihanTable;
